import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  RefreshCw,
  User,
  Gift,
  Coins,
  Play,
  ChevronRight,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { WalletFlow } from "@/components/kudoz/WalletFlow";
import { InputField } from "@/components/kudoz/InputField";
import { StepIndicator } from "@/components/kudoz/StepIndicator";
import { MethodBadge } from "@/components/kudoz/MethodBadge";
import { CodeBlock } from "@/components/kudoz/CodeBlock";
import { RuleTypesList } from "@/components/kudoz/RuleTypesList";
import { ApiSection } from "@/components/kudoz/ApiSection";
import { KudozEndpoints } from "@/components/kudoz/KudozEndpoints";
// import { StepIndicator } from "@/components/kudoz/StepIndicator";
// import { CodeBlock } from "@/components/kudoz/CodeBlock";
// import { ApiSection } from "@/components/kudoz/ApiSection";
// import { MethodBadge } from "@/components/kudoz/MethodBadge";
// import { InputField } from "@/components/kudoz/InputField";
// import { WalletFlow } from "@/components/kudoz/WalletFlow";
// import { RuleTypesList } from "@/components/kudoz/RuleTypesList";

const KUDOZ_BACKEND_URL = import.meta.env.VITE_KUDOZ_BACKEND_URL || "";

function formatJson(value: unknown) {
  if (value === null || value === undefined) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export default function KudozDebug() {
  const { toast } = useToast();
  const { token } = useAuth();
  const [apiKey, setApiKey] = useState("");
  const [activeStep, setActiveStep] = useState(1);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [demoRunning, setDemoRunning] = useState(false);

  // User state
  const [userResponse, setUserResponse] = useState<unknown>(null);
  const [userKudozResponse, setUserKudozResponse] = useState<unknown>(null);
  const [userKudozTransactionsResponse, setUserKudozTransactionsResponse] =
    useState<unknown>(null);
  const [userKudozLimit, setUserKudozLimit] = useState("50");
  const [userKudozOffset, setUserKudozOffset] = useState("0");

  // Earn Rules state
  const [earnRulesPage, setEarnRulesPage] = useState("1");
  const [earnRulesPerPage, setEarnRulesPerPage] = useState("10");
  const [earnRulesRestaurantId, setEarnRulesRestaurantId] = useState("1");
  const [earnRulesResponse, setEarnRulesResponse] = useState<unknown>(null);
  const [createEarnRuleKudozEarnId, setCreateEarnRuleKudozEarnId] =
    useState("1");
  const [createEarnRuleBody, setCreateEarnRuleBody] = useState(
    JSON.stringify(
      {
        kudoz_earn_rule_type_id: 1,
        name: "Example earn rule",
        sort: 1,
        active: true,
        params: { value: {} },
        restaurant_id: 1,
      },
      null,
      2
    )
  );
  const [createEarnRuleResponse, setCreateEarnRuleResponse] =
    useState<unknown>(null);
  const [deleteEarnRuleKudozEarnId, setDeleteEarnRuleKudozEarnId] =
    useState("1");
  const [deleteEarnRuleId, setDeleteEarnRuleId] = useState("");
  const [deleteEarnRuleResponse, setDeleteEarnRuleResponse] =
    useState<unknown>(null);

  // Earn Effects state
  const [earnEffectsKudozEarnId, setEarnEffectsKudozEarnId] = useState("1");
  const [earnEffectsRestaurantId, setEarnEffectsRestaurantId] = useState("1");
  const [earnEffectsResponse, setEarnEffectsResponse] = useState<unknown>(null);
  const [createEarnEffectKudozEarnId, setCreateEarnEffectKudozEarnId] =
    useState("1");
  const [createEarnEffectBody, setCreateEarnEffectBody] = useState(
    JSON.stringify(
      {
        kudoz_earn_effect_type_id: 1,
        name: "Example earn effect",
        sort: 1,
        active: true,
        params: { value: 10 },
        restaurant_id: 1,
      },
      null,
      2
    )
  );
  const [createEarnEffectResponse, setCreateEarnEffectResponse] =
    useState<unknown>(null);
  const [deleteEarnEffectKudozEarnId, setDeleteEarnEffectKudozEarnId] =
    useState("1");
  const [deleteEarnEffectId, setDeleteEarnEffectId] = useState("");
  const [deleteEarnEffectResponse, setDeleteEarnEffectResponse] =
    useState<unknown>(null);

  // Award state
  const [awardBody, setAwardBody] = useState(
    JSON.stringify(
      {
        restaurant_id: 1,
        restaurant_name: "",
        order_id: 1,
        order_date: Math.floor(Date.now() / 1000),
        user_id: 1,
        user_name: "",
        user_email: "",
        service_type: "takeout",
        items: [
          {
            item_id: 1,
            category_id: 1,
            quantity: 1,
            price_per_unit: 100,
            item_total_payment: 100,
          },
        ],
        order_total: 100,
        is_new_customer: false,
        tags: [],
      },
      null,
      2
    )
  );
  const [awardResponse, setAwardResponse] = useState<unknown>(null);

  // Reward Rules state
  const [rewardRulesPage, setRewardRulesPage] = useState("1");
  const [rewardRulesPerPage, setRewardRulesPerPage] = useState("10");
  const [rewardRulesRestaurantId, setRewardRulesRestaurantId] = useState("1");
  const [rewardRulesResponse, setRewardRulesResponse] = useState<unknown>(null);
  const [createRewardRuleKudozRewardId, setCreateRewardRuleKudozRewardId] =
    useState("1");
  const [createRewardRuleBody, setCreateRewardRuleBody] = useState(
    JSON.stringify(
      {
        kudoz_reward_rule_type_id: 1,
        name: "Example reward rule",
        sort: 1,
        active: true,
        params: { value: {} },
        restaurant_id: 1,
      },
      null,
      2
    )
  );
  const [createRewardRuleResponse, setCreateRewardRuleResponse] =
    useState<unknown>(null);
  const [deleteRewardRuleKudozRewardId, setDeleteRewardRuleKudozRewardId] =
    useState("1");
  const [deleteRewardRuleId, setDeleteRewardRuleId] = useState("");
  const [deleteRewardRuleResponse, setDeleteRewardRuleResponse] =
    useState<unknown>(null);

  // Reward Effects state
  const [rewardEffectsKudozRewardId, setRewardEffectsKudozRewardId] =
    useState("1");
  const [rewardEffectsRestaurantId, setRewardEffectsRestaurantId] =
    useState("1");
  const [rewardEffectsResponse, setRewardEffectsResponse] =
    useState<unknown>(null);
  const [createRewardEffectKudozRewardId, setCreateRewardEffectKudozRewardId] =
    useState("1");
  const [createRewardEffectBody, setCreateRewardEffectBody] = useState(
    JSON.stringify(
      {
        kudoz_reward_effect_type_id: 1,
        name: "Example reward effect",
        sort: 1,
        active: true,
        params: { amount: 10 },
        restaurant_id: 1,
      },
      null,
      2
    )
  );
  const [createRewardEffectResponse, setCreateRewardEffectResponse] =
    useState<unknown>(null);
  const [deleteRewardEffectKudozRewardId, setDeleteRewardEffectKudozRewardId] =
    useState("1");
  const [deleteRewardEffectId, setDeleteRewardEffectId] = useState("");
  const [deleteRewardEffectResponse, setDeleteRewardEffectResponse] =
    useState<unknown>(null);

  // Redeem state
  const [rewardRedeemKudozRewardId, setRewardRedeemKudozRewardId] =
    useState("1");
  const [rewardRedeemBody, setRewardRedeemBody] = useState(
    JSON.stringify(
      {
        restaurant_id: 1,
        restaurant_name: "",
        order_id: 1,
        order_date: Math.floor(Date.now() / 1000),
        user_id: 1,
        user_name: "",
        user_email: "",
        service_type: "takeout",
        items: [
          {
            item_id: 1,
            category_id: 1,
            quantity: 1,
            price_per_unit: 100,
            item_total_payment: 100,
          },
        ],
        order_total: 100,
        is_new_customer: false,
        tags: [],
      },
      null,
      2
    )
  );
  const [rewardRedeemResponse, setRewardRedeemResponse] =
    useState<unknown>(null);

  const applyDemoEarnPreset = () => {
    setCreateEarnRuleKudozEarnId("1");
    setDeleteEarnRuleKudozEarnId("1");
    setEarnRulesRestaurantId("1");
    setEarnEffectsKudozEarnId("1");
    setEarnEffectsRestaurantId("1");
    setCreateEarnEffectKudozEarnId("1");
    setDeleteEarnEffectKudozEarnId("1");
    toast({
      title: "Demo IDs Applied",
      description: "All earn fields reset to demo values",
    });
  };

  const applyDemoRewardPreset = () => {
    setCreateRewardRuleKudozRewardId("1");
    setDeleteRewardRuleKudozRewardId("1");
    setRewardRulesRestaurantId("1");
    setRewardEffectsKudozRewardId("1");
    setRewardEffectsRestaurantId("1");
    setDeleteRewardEffectKudozRewardId("1");
    setRewardRedeemKudozRewardId("1");
    toast({
      title: "Demo IDs Applied",
      description: "All reward fields reset to demo values",
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const extractKudozUser = (raw: any) => {
    if (!raw) return null;
    const data = raw.data ?? raw;
    if (!data || typeof data !== "object") return null;

    const candidate =
      data.user && typeof data.user === "object" ? data.user : data;

    const user_id =
      candidate.user_id ?? candidate.id ?? data.user_id ?? data.id;
    const name = candidate.name ?? data.name ?? null;
    const email = candidate.email ?? data.email ?? null;

    if (!user_id && !email) return null;

    return { user_id, name, email };
  };

  const handleCall = async (
    key: string,
    options: { path: string; method?: string; body?: unknown },
    onSuccess: (data: unknown) => void
  ) => {
    setLoadingKey(key);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      if (apiKey) {
        headers["x-services-secret"] = apiKey;
      }

      const method = options.method ?? "GET";
      const fetchOptions: RequestInit = { method, headers };

      if (method === "GET") {
        fetchOptions.cache = "no-store";
      }

      if (options.body !== undefined && method !== "HEAD") {
        fetchOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(options.path, fetchOptions);
      const text = await response.text();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let data: any;
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { raw: text };
      }

      const isSuccess = response.ok || response.status === 304;

      if (!isSuccess) {
        onSuccess(data);
        toast({
          title: "Request failed",
          description: `HTTP ${response.status}`,
          variant: "destructive",
        });
        return;
      }

      onSuccess(data);
      toast({
        title: "Request successful",
        description: `HTTP ${response.status}`,
      });
    } catch (error) {
      onSuccess({
        error: error instanceof Error ? error.message : String(error),
      });
      toast({
        title: "Network error",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoadingKey(null);
    }
  };

  const isLoading = (key: string) => loadingKey === key;

  useEffect(() => {
    if (!token || !KUDOZ_BACKEND_URL) return;
    handleCall(
      "user",
      { path: `${KUDOZ_BACKEND_URL}/users/me` },
      (data) => setUserResponse(data)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUserKudozTransactions = (
    nextLimit?: number,
    nextOffset?: number
  ) => {
    if (!userResponse) {
      toast({
        title: "No user loaded",
        description: "Load the current user first, then fetch transactions.",
        variant: "destructive",
      });
      return;
    }

    const user = extractKudozUser(userResponse);
    if (!user || !user.user_id) {
      toast({
        title: "Missing user_id",
        description: "The /users/me response must include a numeric user_id.",
        variant: "destructive",
      });
      return;
    }

    const limit =
      typeof nextLimit === "number" && Number.isFinite(nextLimit)
        ? nextLimit
        : Number.parseInt(userKudozLimit) || 50;
    const offsetCandidate =
      typeof nextOffset === "number" && Number.isFinite(nextOffset)
        ? nextOffset
        : Number.parseInt(userKudozOffset) || 0;
    const offset = offsetCandidate < 0 ? 0 : offsetCandidate;

    setUserKudozLimit(String(limit));
    setUserKudozOffset(String(offset));

    handleCall(
      "user-kudoz-tx",
      {
        path: `${KUDOZ_BACKEND_URL}/users/${user.user_id}/kudoz-transactions?limit=${limit}&offset=${offset}`,
      },
      (data) => setUserKudozTransactionsResponse(data)
    );
  };

  const runDemoFlow = async () => {
    if (!KUDOZ_BACKEND_URL) {
      toast({
        title: "Backend not configured",
        description: "Set VITE_KUDOZ_BACKEND_URL environment variable.",
        variant: "destructive",
      });
      return;
    }

    if (!token) {
      toast({
        title: "Not authenticated",
        description: "Log in first to run the demo flow.",
        variant: "destructive",
      });
      return;
    }

    if (!apiKey) {
      toast({
        title: "Missing API Token",
        description: "Please enter your x-api-token to perform system actions.",
        variant: "destructive",
      });
      return;
    }

    setDemoRunning(true);

    try {
      // Fetch user first
      let demoUserResponse = userResponse;
      if (!demoUserResponse) {
        await handleCall(
          "user",
          { path: `${KUDOZ_BACKEND_URL}/users/me` },
          (data) => {
            demoUserResponse = data;
            setUserResponse(data);
          }
        );
      }

      if (!demoUserResponse) {
        toast({
          title: "Failed to load user",
          description: "Could not fetch /users/me",
          variant: "destructive",
        });
        return;
      }

      const demoUser = extractKudozUser(demoUserResponse);
      if (!demoUser || !demoUser.user_id) {
        toast({
          title: "Missing user_id",
          description: "User response must include user_id.",
          variant: "destructive",
        });
        return;
      }

      // Parse and update award body
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let parsedAwardBody: any;
      try {
        parsedAwardBody = JSON.parse(awardBody);
      } catch {
        toast({
          title: "Invalid award body",
          description: "Fix the JSON before running demo.",
          variant: "destructive",
        });
        return;
      }

      const awardPayload = {
        ...parsedAwardBody,
        user_id: demoUser.user_id ?? parsedAwardBody.user_id,
        user_name: demoUser.name ?? parsedAwardBody.user_name,
        user_email: demoUser.email ?? parsedAwardBody.user_email,
      };
      setAwardBody(JSON.stringify(awardPayload, null, 2));

      // Call award
      await handleCall(
        "award",
        {
          path: `${KUDOZ_BACKEND_URL}/kudoz-earns/award`,
          method: "POST",
          body: awardPayload,
        },
        (data) => setAwardResponse(data)
      );

      // Fetch balance
      await handleCall(
        "user-kudoz",
        { path: `${KUDOZ_BACKEND_URL}/users/${demoUser.user_id}/kudoz` },
        (data) => setUserKudozResponse(data)
      );

      // Parse and update redeem body
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let parsedRedeemBody: any;
      try {
        parsedRedeemBody = JSON.parse(rewardRedeemBody);
      } catch {
        toast({
          title: "Invalid redeem body",
          description: "Fix the JSON before running demo.",
          variant: "destructive",
        });
        return;
      }

      const redeemPayload = {
        ...parsedRedeemBody,
        user_id: demoUser.user_id ?? parsedRedeemBody.user_id,
        user_name: demoUser.name ?? parsedRedeemBody.user_name,
        user_email: demoUser.email ?? parsedRedeemBody.user_email,
      };
      setRewardRedeemBody(JSON.stringify(redeemPayload, null, 2));

      // Call redeem
      const rewardId =
        Number.parseInt(rewardRedeemKudozRewardId || "1") || 1;
      await handleCall(
        "reward-redeem",
        {
          path: `${KUDOZ_BACKEND_URL}/kudoz-rewards/${rewardId}/redeem`,
          method: "POST",
          body: redeemPayload,
        },
        (data) => setRewardRedeemResponse(data)
      );

      // Refresh balance
      await handleCall(
        "user-kudoz",
        { path: `${KUDOZ_BACKEND_URL}/users/${demoUser.user_id}/kudoz` },
        (data) => setUserKudozResponse(data)
      );

      toast({
        title: "Demo Complete! 🎉",
        description: "Earn → Redeem → Balance refresh completed.",
      });
    } finally {
      setDemoRunning(false);
    }
  };

  // Transaction pagination meta
  const userKudozTransactionsMeta = (() => {
    if (!userKudozTransactionsResponse) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = userKudozTransactionsResponse as any;
    const container = raw.data ?? raw;
    if (!container || typeof container !== "object") return null;

    const total = Number(container.total ?? 0);
    let limit = Number(container.limit);
    if (!Number.isFinite(limit) || limit <= 0)
      limit = Number.parseInt(userKudozLimit) || 50;

    let offset = Number(container.offset);
    if (!Number.isFinite(offset) || offset < 0)
      offset = Number.parseInt(userKudozOffset) || 0;

    const currentPage = Math.floor(offset / limit) + 1;
    const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

    return { total, limit, offset, currentPage, totalPages };
  })();

  const steps = [
    {
      step: 1 as const,
      title: "Load User",
      description:
        "Connect your authenticated user to the Kudoz wallet system",
    },
    {
      step: 2 as const,
      title: "Earn Kudoz",
      description: "Configure rules, effects, and award points to users",
    },
    {
      step: 3 as const,
      title: "Redeem Rewards",
      description: "Set up reward rules and let users redeem their points",
    },
  ];

  return (
    <div className="min-h-screen bg-background fm-fade-up">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-kudoz flex items-center justify-center">
                <Coins className="w-5 h-5 text-kudoz-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">
                  Kudoz API Tester
                </h1>
                <p className="text-sm text-muted-foreground">
                  Loyalty Points Integration Dashboard
                </p>
              </div>
            </div>
            <Button
              onClick={runDemoFlow}
              disabled={demoRunning}
              className="bg-kudoz hover:bg-kudoz-dark text-kudoz-foreground gap-2"
            >
              {demoRunning ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Run Full Demo
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <Card className="mb-6 border-yellow-500/20 bg-yellow-500/5">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-full bg-yellow-500/10 text-yellow-500">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm mb-1">
                System Authentication Required
              </h3>
              <p className="text-xs text-muted-foreground mb-2">
                Some actions (Award, Redeem) require a System API Key
                (x-services-secret).
              </p>
              <div className="mb-3 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-600 dark:text-red-400">
                <strong>SECURITY WARNING:</strong> In production, requests requiring <code>x-services-secret</code> MUST be called from your backend server. NEVER call them directly from the frontend to avoid leaking your Services Secret. This input is for local debugging only.
              </div>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter x-services-secret"
                className="w-full max-w-md px-3 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-kudoz"
              />
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="flow" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
            <TabsTrigger value="flow">Flow Tester</TabsTrigger>
            <TabsTrigger value="api-list">API Reference</TabsTrigger>
          </TabsList>

          <TabsContent value="flow" className="space-y-8">
            {/* Introduction Card */}
        <div>
          <Card className="mb-8 overflow-hidden border-kudoz/20 bg-gradient-to-br from-kudoz-light to-background">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-kudoz/10 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-kudoz" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-1">
                    How Kudoz Points Work
                  </h2>
                  <p className="text-muted-foreground">
                    Points flow between wallets—never created or destroyed. Test
                    the complete earn and redeem flow with your authenticated
                    user.
                  </p>
                </div>
              </div>
              <WalletFlow />
            </CardContent>
          </Card>
        </div>

        {/* Step Navigation */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {steps.map((s) => (
            <button
              key={s.step}
              onClick={() => setActiveStep(s.step)}
              className="text-left"
            >
              <StepIndicator
                step={s.step}
                title={s.title}
                description={s.description}
                isActive={activeStep === s.step}
              />
            </button>
          ))}
        </div>

        {/* Step Content */}
        <div key={activeStep}>
          {/* Step 1: Load User */}
          {activeStep === 1 && (
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-step-1/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-step-1" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        Load Backend User
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Fetch user data from /users/me endpoint
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleCall(
                          "user",
                          { path: `${KUDOZ_BACKEND_URL}/users/me` },
                          (data) => setUserResponse(data)
                        )
                      }
                      disabled={isLoading("user")}
                    >
                      {isLoading("user") ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span className="ml-2">Refresh</span>
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!userResponse) {
                          toast({
                            title: "No user loaded",
                            description: "Click Refresh first",
                            variant: "destructive",
                          });
                          return;
                        }
                        const user = extractKudozUser(userResponse);
                        if (!user) {
                          toast({
                            title: "Could not parse user",
                            variant: "destructive",
                          });
                          return;
                        }
                        try {
                          const parsed = JSON.parse(awardBody);
                          const next = {
                            ...parsed,
                            user_id: user.user_id ?? parsed.user_id,
                            user_name: user.name ?? parsed.user_name,
                            user_email: user.email ?? parsed.user_email,
                          };
                          setAwardBody(JSON.stringify(next, null, 2));
                          toast({
                            title: "User Applied",
                            description: `Using user_id ${user.user_id}`,
                          });
                        } catch {
                          toast({
                            title: "Invalid JSON",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      Use for Award
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>

                {!KUDOZ_BACKEND_URL && (
                  <div className="flex items-center gap-3 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-warning" />
                    <p className="text-sm text-warning">
                      Set VITE_KUDOZ_BACKEND_URL environment variable to enable
                      API calls.
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MethodBadge method="GET" />
                  <code className="px-2 py-1 bg-muted rounded">
                    {KUDOZ_BACKEND_URL || "<BACKEND_URL>"}/users/me
                  </code>
                </div>

                <CodeBlock
                  value={formatJson(userResponse)}
                  label="Response"
                  maxHeight="250px"
                />

                {/* User Kudoz Tools */}
                <div className="pt-4 border-t space-y-4">
                  <h4 className="font-medium text-foreground">
                    User Kudoz Balance & Transactions
                  </h4>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isLoading("user-kudoz")}
                      onClick={() => {
                        const user = extractKudozUser(userResponse);
                        if (!user?.user_id) {
                          toast({
                            title: "Load user first",
                            variant: "destructive",
                          });
                          return;
                        }
                        handleCall(
                          "user-kudoz",
                          {
                            path: `${KUDOZ_BACKEND_URL}/users/${user.user_id}/kudoz`,
                          },
                          (data) => setUserKudozResponse(data)
                        );
                      }}
                    >
                      {isLoading("user-kudoz") ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Get Balance"
                      )}
                    </Button>
                    <div className="flex items-center gap-2">
                      <InputField
                        label=""
                        value={userKudozLimit}
                        onChange={setUserKudozLimit}
                        placeholder="Limit"
                        className="w-20"
                      />
                      <InputField
                        label=""
                        value={userKudozOffset}
                        onChange={setUserKudozOffset}
                        placeholder="Offset"
                        className="w-20"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isLoading("user-kudoz-tx")}
                        onClick={() => fetchUserKudozTransactions()}
                      >
                        {isLoading("user-kudoz-tx") ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Get Transactions"
                        )}
                      </Button>
                    </div>
                  </div>

                  {userKudozTransactionsMeta && (
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>
                        Page {userKudozTransactionsMeta.currentPage} of{" "}
                        {userKudozTransactionsMeta.totalPages} (
                        {userKudozTransactionsMeta.total} total)
                      </span>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={
                            isLoading("user-kudoz-tx") ||
                            userKudozTransactionsMeta.currentPage <= 1
                          }
                          onClick={() => {
                            const m = userKudozTransactionsMeta;
                            fetchUserKudozTransactions(
                              m.limit,
                              Math.max(0, m.offset - m.limit)
                            );
                          }}
                        >
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={
                            isLoading("user-kudoz-tx") ||
                            userKudozTransactionsMeta.currentPage >=
                              userKudozTransactionsMeta.totalPages
                          }
                          onClick={() => {
                            const m = userKudozTransactionsMeta;
                            fetchUserKudozTransactions(
                              m.limit,
                              m.offset + m.limit
                            );
                          }}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <CodeBlock
                      value={formatJson(userKudozResponse)}
                      label="Balance"
                      maxHeight="180px"
                    />
                    <CodeBlock
                      value={formatJson(userKudozTransactionsResponse)}
                      label="Transactions"
                      maxHeight="180px"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Kudoz Earn */}
          {activeStep === 2 && (
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-step-2/10 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-step-2" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        Kudoz Earn System
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Configure rules, effects, and award points
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" onClick={applyDemoEarnPreset}>
                    Use Demo IDs
                  </Button>
                </div>

                <RuleTypesList variant="earn" />

                <Tabs defaultValue="rules" className="space-y-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="rules">Rules</TabsTrigger>
                    <TabsTrigger value="effects">Effects</TabsTrigger>
                    <TabsTrigger value="award">Award</TabsTrigger>
                  </TabsList>

                  <TabsContent value="rules" className="space-y-4">
                    <ApiSection
                      title="List Earn Rules"
                      badge="GET"
                      badgeVariant="success"
                      defaultOpen
                    >
                      <div className="grid md:grid-cols-4 gap-3">
                        <InputField
                          label="kudoz_earn_id"
                          value={createEarnRuleKudozEarnId}
                          onChange={setCreateEarnRuleKudozEarnId}
                          placeholder="1"
                        />
                        <InputField
                          label="restaurant_id"
                          value={earnRulesRestaurantId}
                          onChange={setEarnRulesRestaurantId}
                          placeholder="1"
                        />
                        <InputField
                          label="page"
                          value={earnRulesPage}
                          onChange={setEarnRulesPage}
                          placeholder="1"
                        />
                        <InputField
                          label="per_page"
                          value={earnRulesPerPage}
                          onChange={setEarnRulesPerPage}
                          placeholder="10"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={() => {
                            const earnId =
                              Number.parseInt(createEarnRuleKudozEarnId) || 1;
                            const page =
                              Number.parseInt(earnRulesPage) || 1;
                            const perPage =
                              Number.parseInt(earnRulesPerPage) || 10;
                            const restaurantId = Number.parseInt(
                              earnRulesRestaurantId
                            );
                            const target = Number.isFinite(restaurantId)
                              ? `&restaurant_id=${restaurantId}`
                              : "";
                            handleCall(
                              "earn-rules-list",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-earns/${earnId}/kudoz-earn-rules?page=${page}&per_page=${perPage}${target}`,
                              },
                              (data) => setEarnRulesResponse(data)
                            );
                          }}
                          disabled={isLoading("earn-rules-list")}
                        >
                          {isLoading("earn-rules-list") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Fetch Rules
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(earnRulesResponse)}
                        maxHeight="200px"
                      />
                    </ApiSection>

                    <ApiSection
                      title="Create Earn Rule"
                      badge="POST"
                      badgeVariant="success"
                    >
                      <div className="grid md:grid-cols-2 gap-4">
                        <InputField
                          label="kudoz_earn_id"
                          value={createEarnRuleKudozEarnId}
                          onChange={setCreateEarnRuleKudozEarnId}
                          placeholder="1"
                        />
                        <InputField
                          label="Request Body"
                          value={createEarnRuleBody}
                          onChange={setCreateEarnRuleBody}
                          type="textarea"
                          rows={8}
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={() => {
                            const earnId =
                              Number.parseInt(createEarnRuleKudozEarnId) || 1;
                            let body;
                            try {
                              body = JSON.parse(createEarnRuleBody);
                            } catch {
                              toast({
                                title: "Invalid JSON",
                                variant: "destructive",
                              });
                              return;
                            }
                            handleCall(
                              "earn-rules-create",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-earns/${earnId}/kudoz-earn-rules`,
                                method: "POST",
                                body,
                              },
                              (data) => setCreateEarnRuleResponse(data)
                            );
                          }}
                          disabled={isLoading("earn-rules-create")}
                        >
                          {isLoading("earn-rules-create") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Create Rule
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(createEarnRuleResponse)}
                        maxHeight="150px"
                      />
                    </ApiSection>

                    <ApiSection
                      title="Delete Earn Rule"
                      badge="DELETE"
                      badgeVariant="destructive"
                    >
                      <div className="grid md:grid-cols-3 gap-3">
                        <InputField
                          label="kudoz_earn_id"
                          value={deleteEarnRuleKudozEarnId}
                          onChange={setDeleteEarnRuleKudozEarnId}
                          placeholder="1"
                        />
                        <InputField
                          label="kudoz_earn_rule_id"
                          value={deleteEarnRuleId}
                          onChange={setDeleteEarnRuleId}
                          placeholder="1"
                        />
                        <InputField
                          label="restaurant_id"
                          value={earnRulesRestaurantId}
                          onChange={setEarnRulesRestaurantId}
                          placeholder="1"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          variant="destructive"
                          onClick={() => {
                            const earnId = Number.parseInt(
                              deleteEarnRuleKudozEarnId
                            );
                            const ruleId =
                              Number.parseInt(deleteEarnRuleId);
                            if (
                              !Number.isFinite(earnId) ||
                              !Number.isFinite(ruleId)
                            ) {
                              toast({
                                title: "Missing IDs",
                                variant: "destructive",
                              });
                              return;
                            }
                            const restaurantId = Number.parseInt(
                              earnRulesRestaurantId
                            );
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const body: any = {};
                            if (Number.isFinite(restaurantId)) {
                              body.restaurant_id = restaurantId;
                            }
                            handleCall(
                              "earn-rules-delete",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-earns/${earnId}/kudoz-earn-rules/${ruleId}`,
                                method: "DELETE",
                                body,
                              },
                              (data) => setDeleteEarnRuleResponse(data)
                            );
                          }}
                          disabled={isLoading("earn-rules-delete")}
                        >
                          {isLoading("earn-rules-delete") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Delete Rule
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(deleteEarnRuleResponse)}
                        maxHeight="100px"
                      />
                    </ApiSection>
                  </TabsContent>

                  <TabsContent value="effects" className="space-y-4">
                    <ApiSection
                      title="List Earn Effects"
                      badge="GET"
                      badgeVariant="success"
                      defaultOpen
                    >
                      <div className="grid md:grid-cols-4 gap-3">
                        <InputField
                          label="kudoz_earn_id"
                          value={earnEffectsKudozEarnId}
                          onChange={setEarnEffectsKudozEarnId}
                          placeholder="1"
                        />
                        <InputField
                          label="restaurant_id"
                          value={earnEffectsRestaurantId}
                          onChange={setEarnEffectsRestaurantId}
                          placeholder="1"
                        />
                        <InputField
                          label="page"
                          value={earnRulesPage}
                          onChange={setEarnRulesPage}
                          placeholder="1"
                        />
                        <InputField
                          label="per_page"
                          value={earnRulesPerPage}
                          onChange={setEarnRulesPerPage}
                          placeholder="10"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={() => {
                            const earnId =
                              Number.parseInt(earnEffectsKudozEarnId) || 1;
                            const page =
                              Number.parseInt(earnRulesPage) || 1;
                            const perPage =
                              Number.parseInt(earnRulesPerPage) || 10;
                            const restaurantId = Number.parseInt(
                              earnEffectsRestaurantId
                            );
                            const target = Number.isFinite(restaurantId)
                              ? `&restaurant_id=${restaurantId}`
                              : "";
                            handleCall(
                              "earn-effects-list",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-earns/${earnId}/kudoz-earn-effects?page=${page}&per_page=${perPage}${target}`,
                              },
                              (data) => setEarnEffectsResponse(data)
                            );
                          }}
                          disabled={isLoading("earn-effects-list")}
                        >
                          {isLoading("earn-effects-list") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Fetch Effects
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(earnEffectsResponse)}
                        maxHeight="200px"
                      />
                    </ApiSection>

                    <ApiSection
                      title="Create Earn Effect"
                      badge="POST"
                      badgeVariant="success"
                    >
                      <div className="grid md:grid-cols-2 gap-4">
                        <InputField
                          label="kudoz_earn_id"
                          value={createEarnEffectKudozEarnId}
                          onChange={setCreateEarnEffectKudozEarnId}
                          placeholder="1"
                        />
                        <InputField
                          label="Request Body"
                          value={createEarnEffectBody}
                          onChange={setCreateEarnEffectBody}
                          type="textarea"
                          rows={8}
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={() => {
                            const earnId =
                              Number.parseInt(createEarnEffectKudozEarnId) || 1;
                            let body;
                            try {
                              body = JSON.parse(createEarnEffectBody);
                            } catch {
                              toast({
                                title: "Invalid JSON",
                                variant: "destructive",
                              });
                              return;
                            }
                            handleCall(
                              "earn-effects-create",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-earns/${earnId}/kudoz-earn-effects`,
                                method: "POST",
                                body,
                              },
                              (data) => setCreateEarnEffectResponse(data)
                            );
                          }}
                          disabled={isLoading("earn-effects-create")}
                        >
                          {isLoading("earn-effects-create") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Create Effect
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(createEarnEffectResponse)}
                        maxHeight="150px"
                      />
                    </ApiSection>

                    <ApiSection
                      title="Delete Earn Effect"
                      badge="DELETE"
                      badgeVariant="destructive"
                    >
                      <div className="grid md:grid-cols-3 gap-3">
                        <InputField
                          label="kudoz_earn_id"
                          value={deleteEarnEffectKudozEarnId}
                          onChange={setDeleteEarnEffectKudozEarnId}
                          placeholder="1"
                        />
                        <InputField
                          label="kudoz_earn_effect_id"
                          value={deleteEarnEffectId}
                          onChange={setDeleteEarnEffectId}
                          placeholder="1"
                        />
                        <InputField
                          label="restaurant_id"
                          value={earnEffectsRestaurantId}
                          onChange={setEarnEffectsRestaurantId}
                          placeholder="1"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          variant="destructive"
                          onClick={() => {
                            const earnId = Number.parseInt(
                              deleteEarnEffectKudozEarnId
                            );
                            const effectId =
                              Number.parseInt(deleteEarnEffectId);
                            if (
                              !Number.isFinite(earnId) ||
                              !Number.isFinite(effectId)
                            ) {
                              toast({
                                title: "Missing IDs",
                                variant: "destructive",
                              });
                              return;
                            }
                            const restaurantId = Number.parseInt(
                              earnEffectsRestaurantId
                            );
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const body: any = {};
                            if (Number.isFinite(restaurantId)) {
                              body.restaurant_id = restaurantId;
                            }
                            handleCall(
                              "earn-effects-delete",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-earns/${earnId}/kudoz-earn-effects/${effectId}`,
                                method: "DELETE",
                                body,
                              },
                              (data) => setDeleteEarnEffectResponse(data)
                            );
                          }}
                          disabled={isLoading("earn-effects-delete")}
                        >
                          {isLoading("earn-effects-delete") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Delete Effect
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(deleteEarnEffectResponse)}
                        maxHeight="100px"
                      />
                    </ApiSection>
                  </TabsContent>

                  <TabsContent value="award" className="space-y-4">
                    <ApiSection
                      title="Award Kudoz Points"
                      description="Transfer Kudoz from provider wallet to user wallet based on order data"
                      badge="POST"
                      badgeVariant="success"
                      defaultOpen
                    >
                      <InputField
                        label="Request Body"
                        description="Order payload including user, items, and total"
                        value={awardBody}
                        onChange={setAwardBody}
                        type="textarea"
                        rows={14}
                      />
                      <div className="flex justify-end">
                        <Button
                          size="lg"
                          className="bg-kudoz hover:bg-kudoz-dark text-kudoz-foreground"
                          onClick={() => {
                            let body;
                            try {
                              body = JSON.parse(awardBody);
                            } catch {
                              toast({
                                title: "Invalid JSON",
                                variant: "destructive",
                              });
                              return;
                            }
                            handleCall(
                              "award",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-earns/award`,
                                method: "POST",
                                body,
                              },
                              (data) => setAwardResponse(data)
                            );
                          }}
                          disabled={isLoading("award")}
                        >
                          {isLoading("award") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Gift className="w-4 h-4 mr-2" />
                          )}
                          Award Kudoz
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(awardResponse)}
                        label="Response"
                        maxHeight="200px"
                      />
                    </ApiSection>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Kudoz Reward */}
          {activeStep === 3 && (
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-step-3/10 flex items-center justify-center">
                      <Coins className="w-5 h-5 text-step-3" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        Kudoz Reward System
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Configure redemption rules, effects, and let users spend
                        points
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" onClick={applyDemoRewardPreset}>
                    Use Demo IDs
                  </Button>
                </div>

                <RuleTypesList variant="reward" />

                <Tabs defaultValue="rules" className="space-y-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="rules">Rules</TabsTrigger>
                    <TabsTrigger value="effects">Effects</TabsTrigger>
                    <TabsTrigger value="redeem">Redeem</TabsTrigger>
                  </TabsList>

                  <TabsContent value="rules" className="space-y-4">
                    <ApiSection
                      title="List Reward Rules"
                      badge="GET"
                      badgeVariant="success"
                      defaultOpen
                    >
                      <div className="grid md:grid-cols-4 gap-3">
                        <InputField
                          label="kudoz_reward_id"
                          value={createRewardRuleKudozRewardId}
                          onChange={setCreateRewardRuleKudozRewardId}
                          placeholder="1"
                        />
                        <InputField
                          label="restaurant_id"
                          value={rewardRulesRestaurantId}
                          onChange={setRewardRulesRestaurantId}
                          placeholder="1"
                        />
                        <InputField
                          label="page"
                          value={rewardRulesPage}
                          onChange={setRewardRulesPage}
                          placeholder="1"
                        />
                        <InputField
                          label="per_page"
                          value={rewardRulesPerPage}
                          onChange={setRewardRulesPerPage}
                          placeholder="10"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={() => {
                            const rewardId =
                              Number.parseInt(createRewardRuleKudozRewardId) ||
                              1;
                            const page =
                              Number.parseInt(rewardRulesPage) || 1;
                            const perPage =
                              Number.parseInt(rewardRulesPerPage) || 10;
                            const restaurantId = Number.parseInt(
                              rewardRulesRestaurantId
                            );
                            const target = Number.isFinite(restaurantId)
                              ? `&restaurant_id=${restaurantId}`
                              : "";
                            handleCall(
                              "reward-rules-list",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-rewards/${rewardId}/kudoz-reward-rules?page=${page}&per_page=${perPage}${target}`,
                              },
                              (data) => setRewardRulesResponse(data)
                            );
                          }}
                          disabled={isLoading("reward-rules-list")}
                        >
                          {isLoading("reward-rules-list") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Fetch Rules
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(rewardRulesResponse)}
                        maxHeight="200px"
                      />
                    </ApiSection>

                    <ApiSection
                      title="Create Reward Rule"
                      badge="POST"
                      badgeVariant="success"
                    >
                      <div className="grid md:grid-cols-2 gap-4">
                        <InputField
                          label="kudoz_reward_id"
                          value={createRewardRuleKudozRewardId}
                          onChange={setCreateRewardRuleKudozRewardId}
                          placeholder="1"
                        />
                        <InputField
                          label="Request Body"
                          value={createRewardRuleBody}
                          onChange={setCreateRewardRuleBody}
                          type="textarea"
                          rows={8}
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={() => {
                            const rewardId =
                              Number.parseInt(createRewardRuleKudozRewardId) ||
                              1;
                            let body;
                            try {
                              body = JSON.parse(createRewardRuleBody);
                            } catch {
                              toast({
                                title: "Invalid JSON",
                                variant: "destructive",
                              });
                              return;
                            }
                            handleCall(
                              "reward-rules-create",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-rewards/${rewardId}/kudoz-reward-rules`,
                                method: "POST",
                                body,
                              },
                              (data) => setCreateRewardRuleResponse(data)
                            );
                          }}
                          disabled={isLoading("reward-rules-create")}
                        >
                          {isLoading("reward-rules-create") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Create Rule
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(createRewardRuleResponse)}
                        maxHeight="150px"
                      />
                    </ApiSection>

                    <ApiSection
                      title="Delete Reward Rule"
                      badge="DELETE"
                      badgeVariant="destructive"
                    >
                      <div className="grid md:grid-cols-3 gap-3">
                        <InputField
                          label="kudoz_reward_id"
                          value={deleteRewardRuleKudozRewardId}
                          onChange={setDeleteRewardRuleKudozRewardId}
                          placeholder="1"
                        />
                        <InputField
                          label="kudoz_reward_rule_id"
                          value={deleteRewardRuleId}
                          onChange={setDeleteRewardRuleId}
                          placeholder="1"
                        />
                        <InputField
                          label="restaurant_id"
                          value={rewardRulesRestaurantId}
                          onChange={setRewardRulesRestaurantId}
                          placeholder="1"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          variant="destructive"
                          onClick={() => {
                            const rewardId = Number.parseInt(
                              deleteRewardRuleKudozRewardId
                            );
                            const ruleId =
                              Number.parseInt(deleteRewardRuleId);
                            if (
                              !Number.isFinite(rewardId) ||
                              !Number.isFinite(ruleId)
                            ) {
                              toast({
                                title: "Missing IDs",
                                variant: "destructive",
                              });
                              return;
                            }
                            const restaurantId = Number.parseInt(
                              rewardRulesRestaurantId
                            );
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const body: any = {};
                            if (Number.isFinite(restaurantId)) {
                              body.restaurant_id = restaurantId;
                            }
                            handleCall(
                              "reward-rules-delete",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-rewards/${rewardId}/kudoz-reward-rules/${ruleId}`,
                                method: "DELETE",
                                body,
                              },
                              (data) => setDeleteRewardRuleResponse(data)
                            );
                          }}
                          disabled={isLoading("reward-rules-delete")}
                        >
                          {isLoading("reward-rules-delete") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Delete Rule
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(deleteRewardRuleResponse)}
                        maxHeight="100px"
                      />
                    </ApiSection>
                  </TabsContent>

                  <TabsContent value="effects" className="space-y-4">
                    <ApiSection
                      title="List Reward Effects"
                      badge="GET"
                      badgeVariant="success"
                      defaultOpen
                    >
                      <div className="grid md:grid-cols-4 gap-3">
                        <InputField
                          label="kudoz_reward_id"
                          value={rewardEffectsKudozRewardId}
                          onChange={setRewardEffectsKudozRewardId}
                          placeholder="1"
                        />
                        <InputField
                          label="restaurant_id"
                          value={rewardEffectsRestaurantId}
                          onChange={setRewardEffectsRestaurantId}
                          placeholder="1"
                        />
                        <InputField
                          label="page"
                          value={rewardRulesPage}
                          onChange={setRewardRulesPage}
                          placeholder="1"
                        />
                        <InputField
                          label="per_page"
                          value={rewardRulesPerPage}
                          onChange={setRewardRulesPerPage}
                          placeholder="10"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={() => {
                            const rewardId =
                              Number.parseInt(rewardEffectsKudozRewardId) || 1;
                            const page =
                              Number.parseInt(rewardRulesPage) || 1;
                            const perPage =
                              Number.parseInt(rewardRulesPerPage) || 10;
                            const restaurantId = Number.parseInt(
                              rewardEffectsRestaurantId
                            );
                            const target = Number.isFinite(restaurantId)
                              ? `&restaurant_id=${restaurantId}`
                              : "";
                            handleCall(
                              "reward-effects-list",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-rewards/${rewardId}/kudoz-reward-effects?page=${page}&per_page=${perPage}${target}`,
                              },
                              (data) => setRewardEffectsResponse(data)
                            );
                          }}
                          disabled={isLoading("reward-effects-list")}
                        >
                          {isLoading("reward-effects-list") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Fetch Effects
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(rewardEffectsResponse)}
                        maxHeight="200px"
                      />
                    </ApiSection>

                    <ApiSection
                      title="Create Reward Effect"
                      badge="POST"
                      badgeVariant="success"
                    >
                      <div className="grid md:grid-cols-2 gap-4">
                        <InputField
                          label="kudoz_reward_id"
                          value={createRewardEffectKudozRewardId}
                          onChange={setCreateRewardEffectKudozRewardId}
                          placeholder="1"
                        />
                        <InputField
                          label="Request Body"
                          value={createRewardEffectBody}
                          onChange={setCreateRewardEffectBody}
                          type="textarea"
                          rows={8}
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={() => {
                            const rewardId =
                              Number.parseInt(createRewardEffectKudozRewardId) ||
                              1;
                            let body;
                            try {
                              body = JSON.parse(createRewardEffectBody);
                            } catch {
                              toast({
                                title: "Invalid JSON",
                                variant: "destructive",
                              });
                              return;
                            }
                            handleCall(
                              "reward-effects-create",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-rewards/${rewardId}/kudoz-reward-effects`,
                                method: "POST",
                                body,
                              },
                              (data) => setCreateRewardEffectResponse(data)
                            );
                          }}
                          disabled={isLoading("reward-effects-create")}
                        >
                          {isLoading("reward-effects-create") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Create Effect
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(createRewardEffectResponse)}
                        maxHeight="150px"
                      />
                    </ApiSection>

                    <ApiSection
                      title="Delete Reward Effect"
                      badge="DELETE"
                      badgeVariant="destructive"
                    >
                      <div className="grid md:grid-cols-3 gap-3">
                        <InputField
                          label="kudoz_reward_id"
                          value={deleteRewardEffectKudozRewardId}
                          onChange={setDeleteRewardEffectKudozRewardId}
                          placeholder="1"
                        />
                        <InputField
                          label="kudoz_reward_effect_id"
                          value={deleteRewardEffectId}
                          onChange={setDeleteRewardEffectId}
                          placeholder="1"
                        />
                        <InputField
                          label="restaurant_id"
                          value={rewardEffectsRestaurantId}
                          onChange={setRewardEffectsRestaurantId}
                          placeholder="1"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          variant="destructive"
                          onClick={() => {
                            const rewardId = Number.parseInt(
                              deleteRewardEffectKudozRewardId
                            );
                            const effectId =
                              Number.parseInt(deleteRewardEffectId);
                            if (
                              !Number.isFinite(rewardId) ||
                              !Number.isFinite(effectId)
                            ) {
                              toast({
                                title: "Missing IDs",
                                variant: "destructive",
                              });
                              return;
                            }
                            const restaurantId = Number.parseInt(
                              rewardEffectsRestaurantId
                            );
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const body: any = {};
                            if (Number.isFinite(restaurantId)) {
                              body.restaurant_id = restaurantId;
                            }
                            handleCall(
                              "reward-effects-delete",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-rewards/${rewardId}/kudoz-reward-effects/${effectId}`,
                                method: "DELETE",
                                body,
                              },
                              (data) => setDeleteRewardEffectResponse(data)
                            );
                          }}
                          disabled={isLoading("reward-effects-delete")}
                        >
                          {isLoading("reward-effects-delete") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Delete Effect
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(deleteRewardEffectResponse)}
                        maxHeight="100px"
                      />
                    </ApiSection>
                  </TabsContent>

                  <TabsContent value="redeem" className="space-y-4">
                    <ApiSection
                      title="Redeem Reward"
                      description="Transfer Kudoz from user wallet to restaurant wallet and apply reward benefits"
                      badge="POST"
                      badgeVariant="success"
                      defaultOpen
                    >
                      <InputField
                        label="kudoz_reward_id"
                        value={rewardRedeemKudozRewardId}
                        onChange={setRewardRedeemKudozRewardId}
                        placeholder="1"
                        className="max-w-xs"
                      />
                      <InputField
                        label="Request Body"
                        description="Order payload for redemption"
                        value={rewardRedeemBody}
                        onChange={setRewardRedeemBody}
                        type="textarea"
                        rows={14}
                      />
                      <div className="flex justify-end">
                        <Button
                          size="lg"
                          className="bg-success hover:bg-success/90 text-success-foreground"
                          onClick={() => {
                            if (!apiKey) {
                              toast({
                                title: "Missing API Key",
                                description:
                                  "Please enter your x-services-secret to perform system actions.",
                                variant: "destructive",
                              });
                              return;
                            }
                            const rewardId =
                              Number.parseInt(rewardRedeemKudozRewardId) || 1;
                            let body;
                            try {
                              body = JSON.parse(rewardRedeemBody);
                            } catch {
                              toast({
                                title: "Invalid JSON",
                                variant: "destructive",
                              });
                              return;
                            }
                            handleCall(
                              "reward-redeem",
                              {
                                path: `${KUDOZ_BACKEND_URL}/kudoz-rewards/${rewardId}/redeem`,
                                method: "POST",
                                body,
                              },
                              (data) => setRewardRedeemResponse(data)
                            );
                          }}
                          disabled={isLoading("reward-redeem")}
                        >
                          {isLoading("reward-redeem") ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Coins className="w-4 h-4 mr-2" />
                          )}
                          Redeem Reward
                        </Button>
                      </div>
                      <CodeBlock
                        value={formatJson(rewardRedeemResponse)}
                        label="Response"
                        maxHeight="200px"
                      />
                    </ApiSection>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
        </TabsContent>

        <TabsContent value="api-list">
          <KudozEndpoints />
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}
